// Change-type taxonomy for the Web Changes log — mirrors vault/categories.js.
// One place to add/rename/recolor a type. Keys are stored on each row as
// `change_type`; labels + colors drive the badges and filter pills.
export const CATEGORIES = [
  { key: 'website', label: 'Website' },
  { key: 'ab', label: 'A/B Testing' },
  { key: 'cro', label: 'CRO' },
  { key: 'inventory', label: 'Inventory' },
  { key: 'pricing', label: 'Pricing & Market' },
  { key: 'shipping', label: 'Shipping' },
  { key: 'subscriptions', label: 'Subscriptions' },
  { key: 'email', label: 'Email & Klaviyo' },
  { key: 'copy', label: 'Copy' },
  { key: 'sku', label: 'SKU & Barcode' },
  { key: 'tracking', label: 'Tracking' },
  { key: 'payment', label: 'Payment' },
  { key: 'admin', label: 'Admin / Account' },
  { key: 'other', label: 'Other' },
];

export const CATEGORY_COLORS = {
  website: '#2563eb',
  ab: '#db2777',
  cro: '#c026d3',
  inventory: '#ca8a04',
  pricing: '#16a34a',
  shipping: '#0891b2',
  subscriptions: '#7c3aed',
  email: '#0d9488',
  copy: '#f97316',
  sku: '#4f46e5',
  tracking: '#0f766e',
  payment: '#dc2626',
  admin: '#475569',
  other: '#64748b',
};

// Shown as a hint under the Type field when adding/editing a change.
export const CATEGORY_EXAMPLES = {
  website: 'New pages/landers, PDPs, cart, theme, site improvements',
  ab: 'Starting/pausing A/B tests, traffic splits',
  cro: 'On-page conversion tweaks, section order, offer copy',
  inventory: 'In-stock / OOS toggles, availability, backlog swaps',
  pricing: 'Market price updates, currency/market changes',
  shipping: 'Shipping rules, ETAs, express toggles',
  subscriptions: 'Skio settings, loyalty, cancellation flows',
  email: 'Klaviyo flows/popups, sender domains, forms',
  copy: 'Listicle/PDP/page copy changes',
  sku: 'SKU renames, barcodes',
  tracking: 'GA4, Elevar, pixels, attribution',
  payment: 'PayPal/gateway enable-disable',
  admin: 'Accounts, domains, email setup, apps, access',
  other: "Anything that doesn't fit",
};

const CAT_KEYS = new Set(CATEGORIES.map(c => c.key));
export function labelFor(key) {
  return (CATEGORIES.find(c => c.key === key) || {}).label || 'Other';
}
export function colorFor(key) {
  return CATEGORY_COLORS[key] || CATEGORY_COLORS.other;
}

// Groups changes into taxonomy-ordered sections, skipping empties.
// (Not used by the flat-table primary view, but handy for counts/filter.)
export function groupByCategory(items) {
  return CATEGORIES
    .map(cat => ({
      key: cat.key,
      label: cat.label,
      color: colorFor(cat.key),
      items: items.filter(c => (CAT_KEYS.has(c.change_type) ? c.change_type : 'other') === cat.key),
    }))
    .filter(g => g.items.length > 0);
}

// Maps the raw CSV "Type" strings → a category key. Used by the later CSV
// backfill; kept here so the mapping lives with the taxonomy.
export function mapRawType(raw) {
  const s = (raw || '').toLowerCase();
  if (!s) return 'other';
  if (s.includes('a/b')) return 'ab';
  if (s.includes('cro')) return 'cro';
  if (s.includes('inventory')) return 'inventory';
  if (s.includes('price') || s.includes('market')) return 'pricing';
  if (s.includes('shipping')) return 'shipping';
  if (s.includes('skio') || s.includes('subscription')) return 'subscriptions';
  if (s.includes('klaviyo') || s.includes('email') || s.includes('popup')) return 'email';
  if (s.includes('copy') || s.includes('listicle')) return 'copy';
  if (s.includes('sku') || s.includes('barcode')) return 'sku';
  if (s.includes('tracking')) return 'tracking';
  if (s.includes('payment')) return 'payment';
  if (s.includes('admin') || s.includes('account') || s.includes('domain') || s.includes('app')) return 'admin';
  if (s.includes('page') || s.includes('lander') || s.includes('pdp') || s.includes('web') || s.includes('site') || s.includes('hp') || s.includes('offers')) return 'website';
  return 'other';
}
